'use client';

import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { delocalizePath } from '@/lib/i18n-routes';
import type {
  CompanyViewInput,
  RecentCompanyViewItem,
  RecentSearchInput,
  RecentSearchItem,
} from '@/lib/recent-activity-types';
import { useSession } from '@/hooks/use-session';

export const RECENT_VIEWS_QUERY_KEY = ['account', 'recent-views'] as const;
export const RECENT_SEARCHES_QUERY_KEY = [
  'account',
  'recent-searches',
] as const;

const viewsResponseSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        euId: z.string(),
        companyName: z.string(),
        subtitle: z.string().nullable(),
        countryCode: z.string().nullable(),
        path: z.string(),
        viewCount: z.number().int(),
        lastViewedAt: z.iso.datetime(),
      }),
    ),
  }),
});
const searchesResponseSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        id: z.string(),
        url: z.string(),
        filters: z.record(z.string(), z.json()),
        resultCount: z.number().int().nullable(),
        runCount: z.number().int(),
        lastRunAt: z.iso.datetime(),
      }),
    ),
  }),
});

export function useRecentCompanyViews(limit = 10) {
  const { data: session } = useSession();
  const { data, isLoading, isError, refetch } = useQuery<RecentCompanyViewItem[]>({
    queryKey: [...RECENT_VIEWS_QUERY_KEY, limit],
    queryFn: async () => {
      const response = await fetch(`/api/account/recent-views?limit=${limit}`);
      if (!response.ok)
        throw new Error(`Recent views returned ${response.status}`);
      return viewsResponseSchema.parse(await response.json()).data.items;
    },
    enabled: !!session?.user?.sub,
    staleTime: 30_000,
  });
  return { items: data ?? [], isLoading, isError, refetch };
}

export function useRecentSearches(limit = 8) {
  const { data: session } = useSession();
  const { data, isLoading, isError, refetch } = useQuery<RecentSearchItem[]>({
    queryKey: [...RECENT_SEARCHES_QUERY_KEY, limit],
    queryFn: async () => {
      const response = await fetch(
        `/api/account/recent-searches?limit=${limit}`,
      );
      if (!response.ok)
        throw new Error(`Recent searches returned ${response.status}`);
      return searchesResponseSchema.parse(await response.json()).data.items;
    },
    enabled: !!session?.user?.sub,
    staleTime: 30_000,
  });
  return { items: data ?? [], isLoading, isError, refetch };
}

async function postActivity(path: string, input: unknown) {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    keepalive: true,
  });
  if (!response.ok) throw new Error(`${path} returned ${response.status}`);
}

export function useRecordCompanyView() {
  const queryClient = useQueryClient();
  return useCallback(
    (input: CompanyViewInput) => {
      void postActivity('/api/account/recent-views', input)
        .then(() =>
          queryClient.invalidateQueries({ queryKey: RECENT_VIEWS_QUERY_KEY }),
        )
        .catch(() => {});
    },
    [queryClient],
  );
}

export function useRecordRecentSearch() {
  const queryClient = useQueryClient();
  return useCallback(
    (input: RecentSearchInput) => {
      void postActivity('/api/account/recent-searches', input)
        .then(() =>
          queryClient.invalidateQueries({
            queryKey: RECENT_SEARCHES_QUERY_KEY,
          }),
        )
        .catch(() => {});
    },
    [queryClient],
  );
}

const importedUsers = new Set<string>();
const legacyCompanySchema = z.object({
  type: z.literal('company'),
  id: z.string().min(1).max(64),
  title: z.string().min(1).max(300),
  url: z.string(),
  timestamp: z
    .number()
    .int()
    .refine(
      (value) =>
        value <= Date.now() && Number.isFinite(new Date(value).getTime()),
    ),
});

/** Called by the Overview page; once per user per page load, including remounts. */
export function useImportLegacyHistory() {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const sub = session?.user?.sub;
  const email = session?.user?.email;

  useEffect(() => {
    if (!sub || !email || importedUsers.has(sub)) return;
    importedUsers.add(sub);
    const key = `user_personal_history_${email}`;
    void (async () => {
      try {
        const saved = localStorage.getItem(key);
        if (!saved) return;
        const legacyItems = z.array(z.unknown()).parse(JSON.parse(saved));
        const items = legacyItems
          .flatMap((entry) => {
            const parsed = legacyCompanySchema.safeParse(entry);
            if (!parsed.success) return [];
            const item = parsed.data;
            const path = delocalizePath(item.url).pathname;
            if (!path.startsWith('/company/') || path.length > 500) return [];
            return [
              {
                euId: item.id,
                companyName: item.title,
                subtitle: null,
                countryCode: null,
                path,
                viewedAt: new Date(item.timestamp).toISOString(),
              },
            ];
          })
          .slice(0, 20);
        await postActivity('/api/account/recent-views', { import: items });
        localStorage.removeItem(key);
        await queryClient.invalidateQueries({
          queryKey: RECENT_VIEWS_QUERY_KEY,
        });
      } catch {
        // Keep legacy data on failed imports or unavailable localStorage.
      }
    })();
  }, [sub, email, queryClient]);
}
